import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, unlockScroll } from '@theme/utilities';

const OPEN_CLASS = 'br-toolbar--filters-drawer-open';

/**
 * Layers the theme drawer behaviour contract onto the Bloomreach Loomi
 * collections widget's filters drawer, in place.
 *
 * The widget's drawer is a `position: fixed` div toggled by a class on
 * `.br-facets-container` — no dialog semantics, focus trap, scroll lock,
 * Escape handling or backdrop. Its facet interactions rely on delegated
 * listeners above the drawer node, so the drawer cannot be adopted into a
 * real `<theme-drawer>` without silently breaking search triggering
 * (verified: moving `.br-facets__container` or the whole drawer node keeps
 * option clicks toggling visually but no dxpapi search ever fires).
 *
 * This element therefore leaves the widget DOM untouched and:
 * - drives open/close through synthetic clicks on the widget's own controls,
 *   so the bubble path the widget depends on stays intact
 * - mirrors the widget's open-class into scroll lock, focus trap, Escape
 *   handling, a backdrop, dialog ARIA and `aria-expanded` on the toggle
 * - exposes `open()`/`close()`/`toggle()` so Horizon event bindings
 *   (`on:click="#plp-filters-panel/toggle"`) can address it like any drawer
 *
 * It is inert on pages without a widget (the `.br-widget-host--*` class is
 * server-rendered by the app block, so presence is known at connect). Both
 * the collections and search widgets render the same drawer markup, so one
 * controller serves both page types.
 */
class BloomreachFiltersPanel extends Component {
  /** @type {Element | null} The widget's `.br-facets-container` toolbar element. */
  #container = null;
  /** @type {MutationObserver | null} */
  #chipObserver = null;

  /** @type {HTMLElement | null} */
  #widgetToggle = null;

  /** @type {HTMLElement | null} */
  #drawer = null;

  /** @type {MutationObserver | undefined} Watches the widget host until the toolbar renders. */
  #renderObserver;

  /** @type {MutationObserver | undefined} Mirrors the widget's open-class into behaviour. */
  #openStateObserver;

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  #isOpen = false;

  connectedCallback() {
    super.connectedCallback();

    const host = document.querySelector('.br-widget-host--collections, .br-widget-host--search');
    if (!host) return;

    this.#renderObserver = new MutationObserver(() => this.#enhance());
    this.#renderObserver.observe(host, { childList: true, subtree: true });
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#renderObserver?.disconnect();
    this.#openStateObserver?.disconnect();
    if (this.#isOpen) this.#onClose();
  }

  /** Opens the filters panel through the widget's own toggle. */
  open() {
    if (!this.#isOpen) this.#widgetToggle?.click();
  }

  /** Closes the filters panel through the widget's own close button. */
  close() {
    if (!this.#isOpen) return;
    const closeButton = this.#drawer?.querySelector('.br-toolbar__filters-drawer-close');
    if (closeButton instanceof HTMLElement) {
      closeButton.click();
    } else {
      this.#widgetToggle?.click();
    }
  }

  toggle() {
    this.#isOpen ? this.close() : this.open();
  }

  /**
   * Wires the widget's toolbar once it exists. Re-runs from the render
   * observer so a widget re-render that replaces the toolbar re-enhances.
   */
  #enhance() {
    if (this.#container?.isConnected && this.#drawer?.isConnected) {
      this.#relocateAppliedFilters();
      this.#syncFooterCopy();
      return;
    }

    const container = document.querySelector('.br-facets-container.br-toolbar');
    const widgetToggle = container?.querySelector('.br-toolbar__filters-toggle');
    const drawer = container?.querySelector('.br-toolbar__filters-drawer');
    if (!container || !(widgetToggle instanceof HTMLElement) || !(drawer instanceof HTMLElement)) return;

    this.#openStateObserver?.disconnect();
    this.#container = container;
    this.#widgetToggle = widgetToggle;
    this.#drawer = drawer;

    this.#relocateAppliedFilters();
    this.#syncFooterCopy();

    drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    const title = drawer.querySelector('.br-toolbar__filters-drawer-title');
    if (title) {
      if (title.getAttribute('aria-hidden') !== 'true') title.setAttribute('aria-hidden', 'true');
    }
    if (drawer.hasAttribute('aria-labelledby')) drawer.removeAttribute('aria-labelledby');
    if (drawer.getAttribute('aria-label') !== 'Filters') drawer.setAttribute('aria-label', 'Filters');

    widgetToggle.setAttribute('aria-haspopup', 'dialog');
    widgetToggle.setAttribute('aria-expanded', 'false');

    if (!container.querySelector('.bloomreach-filters-panel__backdrop')) {
      const backdrop = document.createElement('div');
      backdrop.className = 'bloomreach-filters-panel__backdrop';
      backdrop.addEventListener('click', () => this.close());
      drawer.before(backdrop);
    }

    this.#openStateObserver = new MutationObserver(() => this.#syncOpenState());
    this.#openStateObserver.observe(container, { attributes: true, attributeFilter: ['class'] });
    this.#syncOpenState();
  }

  /**
   * Keeps Loomi's existing applied-facet row inside the drawer footer. The
   * widget owns the row's live event handlers, so reparent the original node
   * rather than cloning it or rebuilding its chips.
   */
  #relocateAppliedFilters() {
    const drawer = this.#drawer;
    const footer = drawer?.querySelector('.br-toolbar__filters-drawer-footer');
    const widgetRoot = this.#container?.closest('.br-ceres-collection') ?? document;
    const applied = widgetRoot.querySelector('.br-facets__selected');

    if (!(footer instanceof HTMLElement) || !(applied instanceof HTMLElement)) return;

    const redundantClear = applied.querySelector('.br-facets__clear-all');
    if (redundantClear instanceof HTMLButtonElement) {
      if (!redundantClear.hidden) redundantClear.hidden = true;
      if (redundantClear.getAttribute('aria-hidden') !== 'true') {
        redundantClear.setAttribute('aria-hidden', 'true');
      }
    }

    if (applied.parentElement !== footer) footer.prepend(applied);
    this.#trimChipLabels(applied);
  }

  /**
   * Shows each applied chip as its value alone ("Pink", not "Colour: Pink"),
   * per the D-Filter design. The remove button keeps the full "Remove
   * Colour: Pink" label, so assistive tech still hears which facet it is.
   * Loomi renders the chip into a shadow root and re-renders it on change,
   * so the trim is idempotent and re-applied from the row's mutations.
   * @param {HTMLElement} applied - The applied-facet row.
   */
  #trimChipLabels(applied) {
    for (const chip of applied.querySelectorAll('br-facet-chip')) {
      const label = (chip.shadowRoot ?? chip).querySelector('.br-facet-chip__label');
      if (!(label instanceof HTMLElement)) continue;
      const text = label.textContent ?? '';
      const separator = text.indexOf(': ');
      if (separator === -1) continue;
      const value = text.slice(separator + 2).trim();
      if (value && label.textContent !== value) label.textContent = value;
    }

    if (this.#chipObserver) return;
    this.#chipObserver = new MutationObserver(() => this.#trimChipLabels(applied));
    this.#chipObserver.observe(applied, { childList: true, subtree: true, characterData: true });
  }

  /**
   * Aligns Bloomreach's widget-owned footer copy with the Ceres filter panel.
   * The result count is retained from the widget's current "See N products"
   * label before the action is renamed to "View Selection".
   */
  #syncFooterCopy() {
    const footer = this.#drawer?.querySelector('.br-toolbar__filters-drawer-footer');
    const clear = footer?.querySelector('.br-toolbar__filters-drawer-clear');
    const apply = footer?.querySelector('.br-toolbar__filters-drawer-apply');

    if (!(footer instanceof HTMLElement)) return;

    if (clear instanceof HTMLButtonElement && clear.textContent?.trim() !== 'Clear All') {
      clear.textContent = 'Clear All';
      const icon = this.#cloneClearIcon();
      if (icon) clear.append(icon);
    }

    if (!(apply instanceof HTMLButtonElement)) return;

    const countMatch = apply.textContent?.match(/\d[\d,]*/);
    if (countMatch && footer.dataset.brResultCount !== countMatch[0]) {
      footer.dataset.brResultCount = countMatch[0];
    }

    let meta = footer.querySelector('.br-toolbar__filters-drawer-meta');
    if (!(meta instanceof HTMLElement)) {
      meta = document.createElement('div');
      meta.className = 'br-toolbar__filters-drawer-meta';
      footer.insertBefore(meta, apply);
    }

    const resultCount = footer.dataset.brResultCount;
    if (resultCount) {
      let count = meta.querySelector('.br-toolbar__filters-drawer-count');
      if (!(count instanceof HTMLElement)) {
        count = document.createElement('span');
        count.className = 'br-toolbar__filters-drawer-count';
        meta.append(count);
      }
      if (count.textContent !== `${resultCount} Results`) {
        count.textContent = `${resultCount} Results`;
      }

      // Keep the result count on the left, with the live clear action on the right.
      if (meta.firstElementChild !== count) meta.prepend(count);
    }

    if (clear instanceof HTMLElement && clear.parentElement !== meta) meta.append(clear);

    if (apply.textContent?.trim() !== 'View Selection') apply.textContent = 'View Selection';
  }

  /**
   * Clones the close icon markup from the panel's icon template.
   * @returns {Node | null} The cloned icon, or null if the template/icon isn't found.
   */
  #cloneClearIcon() {
    const template = document.getElementById('bloomreach-filters-panel-icons');
    if (!(template instanceof HTMLTemplateElement)) return null;
    return template.content.querySelector('.br-toolbar__filters-drawer-clear-icon')?.cloneNode(true) ?? null;
  }

  /** Mirrors the widget's drawer-open class into the behaviour layer. */
  #syncOpenState() {
    const isOpen = Boolean(this.#container?.classList.contains(OPEN_CLASS));
    if (isOpen === this.#isOpen) return;

    this.#isOpen = isOpen;
    isOpen ? this.#onOpen() : this.#onClose();
  }

  #onOpen() {
    const drawer = this.#drawer;
    if (!drawer) return;

    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#widgetToggle?.setAttribute('aria-expanded', 'true');
    lockScroll(this);
    trapFocus(drawer);

    // The open class lands a mutation-observer microtask before style recalc,
    // while the drawer still computes visibility: hidden — a synchronous
    // focus() silently fails. Defer a frame, and yield if focus already
    // settled inside the drawer (the widget runs its own focus sentinels).
    requestAnimationFrame(() => {
      if (!this.#isOpen) return;
      if (drawer.contains(document.activeElement)) return;
      const closeButton = drawer.querySelector('.br-toolbar__filters-drawer-close');
      if (closeButton instanceof HTMLElement) closeButton.focus();
    });

    document.addEventListener('keydown', this.#onKeyDown);
  }

  #onClose() {
    this.#widgetToggle?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.#onKeyDown);
    removeTrapFocus();
    unlockScroll(this);

    const focusTarget = this.#previouslyFocused?.isConnected ? this.#previouslyFocused : this.#widgetToggle;
    focusTarget?.focus();
    this.#previouslyFocused = null;
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return;
    if (!this.#isOpen) return;

    event.preventDefault();
    this.close();
  };
}

if (!customElements.get('bloomreach-filters-panel')) {
  customElements.define('bloomreach-filters-panel', BloomreachFiltersPanel);
}
